import { useEffect, useState } from 'react'
import type { EmailOtpType } from '@supabase/supabase-js'
import { useLocation, useNavigate } from 'react-router-dom'
import { Button } from '../components/Button'
import { Card } from '../components/Card'
import { ToastProvider, useToast } from '../components/Toast'
import { supabase } from '../lib/supabase'
import { useDb, getRoleHomePath, getSessionUser } from '../state/DbProvider'
import { readAuthCallbackParams } from '../utils/authCallback'
import {
  CONNECTOR_EMAIL_CONFIRMED_NOTICE,
  CONNECTOR_SIGNUP_EMAIL_STORAGE_KEY,
  CONNECTOR_SIGNUP_PENDING_NOTICE,
} from '../utils/connectorSignup'

type ConnectorSignupCompleteState = {
  signupNotice?: string
  signupEmail?: string
}

function getStoredSignupEmail() {
  if (typeof window === 'undefined') return ''
  return window.localStorage.getItem(CONNECTOR_SIGNUP_EMAIL_STORAGE_KEY) ?? ''
}

function setStoredSignupEmail(email: string) {
  if (typeof window === 'undefined') return
  if (email.trim()) {
    window.localStorage.setItem(CONNECTOR_SIGNUP_EMAIL_STORAGE_KEY, email.trim())
    return
  }
  window.localStorage.removeItem(CONNECTOR_SIGNUP_EMAIL_STORAGE_KEY)
}

function ConnectorSignupCompleteInner() {
  const { db, actions } = useDb()
  const toast = useToast()
  const location = useLocation()
  const navigate = useNavigate()
  const flashState = (location.state as ConnectorSignupCompleteState | null) ?? null
  const initialEmail = flashState?.signupEmail ?? getStoredSignupEmail()
  const hasSignupFlash = Boolean(flashState?.signupNotice || flashState?.signupEmail)

  const [signupEmail, setSignupEmail] = useState(initialEmail)
  const [signupNotice, setSignupNotice] = useState(
    flashState?.signupNotice ?? (initialEmail ? CONNECTOR_SIGNUP_PENDING_NOTICE : ''),
  )
  const [password, setPassword] = useState('')
  const [email, setEmail] = useState(initialEmail)
  const [confirmationStatus, setConfirmationStatus] = useState<'idle' | 'processing' | 'confirmed' | 'error'>(
    'idle',
  )
  const [confirmationMessage, setConfirmationMessage] = useState('')

  const already = db ? getSessionUser(db) : null
  const callbackParams = readAuthCallbackParams(location)
  const callbackCode = callbackParams.code
  const callbackType = callbackParams.type
  const callbackTokenHash = callbackParams.tokenHash
  const callbackAccessToken = callbackParams.accessToken
  const callbackRefreshToken = callbackParams.refreshToken
  const hasConfirmationParams = Boolean(
    callbackCode || callbackTokenHash || callbackAccessToken || callbackRefreshToken,
  )

  useEffect(() => {
    if (!already || hasConfirmationParams || confirmationStatus !== 'idle' || hasSignupFlash) return
    navigate(getRoleHomePath(already), { replace: true })
  }, [already, confirmationStatus, hasConfirmationParams, hasSignupFlash, navigate])

  useEffect(() => {
    if (!flashState?.signupNotice && !flashState?.signupEmail) return
    navigate(location.pathname, { replace: true, state: null })
  }, [flashState?.signupEmail, flashState?.signupNotice, location.pathname, navigate])

  useEffect(() => {
    if (!initialEmail) return
    setStoredSignupEmail(initialEmail)
  }, [initialEmail])

  useEffect(() => {
    let cancelled = false

    const clearSignupSession = async () => {
      if (!already || !hasSignupFlash) return

      setConfirmationStatus('processing')
      setConfirmationMessage('登録後のログイン画面を準備しています...')

      const { error } = await supabase.auth.signOut({ scope: 'local' })
      if (cancelled) return

      if (error) {
        setConfirmationStatus('error')
        setConfirmationMessage('登録直後のセッション解除に失敗しました。ページを再読み込みしてください。')
        return
      }

      setConfirmationStatus('idle')
      setConfirmationMessage('')
    }

    void clearSignupSession()

    return () => {
      cancelled = true
    }
  }, [already, hasSignupFlash])

  useEffect(() => {
    let cancelled = false

    const prepareConfirmationSession = async () => {
      if (!hasConfirmationParams) return

      const fail = (message: string) => {
        if (cancelled) return
        setConfirmationStatus('error')
        setConfirmationMessage(message)
      }

      const succeed = (message: string) => {
        if (cancelled) return
        setConfirmationStatus('confirmed')
        setConfirmationMessage(message)
        setSignupNotice((current) => current || CONNECTOR_SIGNUP_PENDING_NOTICE)
      }

      setConfirmationStatus('processing')
      setConfirmationMessage('メール認証を確認しています...')

      try {
        const initialSession = await supabase.auth.getSession()
        if (initialSession.error) {
          fail(initialSession.error.message)
          return
        }

        if (initialSession.data.session) {
          const { error } = await supabase.auth.signOut({ scope: 'local' })
          if (error) {
            fail(error.message)
            return
          }
        }

        if (callbackCode) {
          const { error } = await supabase.auth.exchangeCodeForSession(callbackCode)
          if (error) {
            fail(error.message)
            return
          }
        } else if (callbackTokenHash && callbackType) {
          const { error } = await supabase.auth.verifyOtp({
            token_hash: callbackTokenHash,
            type: callbackType as EmailOtpType,
          })
          if (error) {
            fail(error.message)
            return
          }
        } else if (callbackAccessToken && callbackRefreshToken) {
          const { error } = await supabase.auth.setSession({
            access_token: callbackAccessToken,
            refresh_token: callbackRefreshToken,
          })
          if (error) {
            fail(error.message)
            return
          }
        }

        const { data, error } = await supabase.auth.getSession()
        if (error) {
          fail(error.message)
          return
        }

        window.history.replaceState({}, document.title, location.pathname)

        if (data.session?.user?.email) {
          setSignupEmail(data.session.user.email)
          setEmail(data.session.user.email)
          setStoredSignupEmail(data.session.user.email)
        }

        if (!data.session?.user) {
          fail('メール認証のセッションを確認できませんでした。確認メールのリンクを開き直してください。')
          return
        }

        const { error: signOutError } = await supabase.auth.signOut({ scope: 'local' })
        if (signOutError) {
          fail(signOutError.message)
          return
        }

        setSignupNotice(CONNECTOR_EMAIL_CONFIRMED_NOTICE)
        succeed(CONNECTOR_EMAIL_CONFIRMED_NOTICE)
      } catch (error) {
        fail(error instanceof Error ? error.message : 'メール認証の確認に失敗しました。')
      }
    }

    void prepareConfirmationSession()

    return () => {
      cancelled = true
    }
  }, [
    callbackAccessToken,
    callbackCode,
    callbackRefreshToken,
    callbackTokenHash,
    callbackType,
    hasConfirmationParams,
    location.pathname,
  ])

  if (already && confirmationStatus === 'idle' && !hasConfirmationParams && !hasSignupFlash) return null

  const bannerToneClass =
    confirmationStatus === 'error'
      ? 'border-rose-200 bg-rose-50 text-rose-700'
      : 'border-emerald-200 bg-emerald-50 text-emerald-900'
  const canLogin = confirmationStatus !== 'processing'

  return (
    <div className="mx-auto flex min-h-screen w-full max-w-md items-center px-4 py-8">
      <Card
        className="w-full"
        title="コネクター登録受付"
        subtitle="メール認証後、この画面からメールアドレスとパスワードでログインしてください。代理店申請は不要です。"
      >
        <div className="grid gap-4 px-5 py-4">
          {signupNotice ? (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900">
              <div className="font-semibold">コネクター登録を受け付けました</div>
              <div className="mt-1 leading-6">{signupNotice}</div>
              {signupEmail ? <div className="mt-1 text-xs text-emerald-700">送信先: {signupEmail}</div> : null}
            </div>
          ) : null}

          {confirmationStatus !== 'idle' ? (
            <div className={`rounded-lg border p-3 text-sm ${bannerToneClass}`}>
              <div className="font-semibold">
                {confirmationStatus === 'error' ? 'メール認証を確認できませんでした' : 'メール認証の状態'}
              </div>
              <div className="mt-1 leading-6">{confirmationMessage}</div>
            </div>
          ) : null}

          <label className="grid gap-1 text-sm">
            <span className="text-slate-700">メールアドレス</span>
            <input
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2"
              placeholder="you@example.com"
              type="email"
            />
          </label>

          <label className="grid gap-1 text-sm">
            <span className="text-slate-700">パスワード</span>
            <input
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2"
              placeholder="••••••••"
              type="password"
            />
          </label>

          <Button
            variant="primary"
            className="w-full"
            disabled={!canLogin}
            onClick={async () => {
              const result = await actions.login(email, password)
              if (!result.ok) {
                toast.show(result.message ?? 'ログインに失敗しました', 'error')
                return
              }
              setStoredSignupEmail('')
              navigate('/')
            }}
          >
            ログイン
          </Button>
        </div>
      </Card>
    </div>
  )
}

export default function ConnectorSignupCompletePage() {
  return (
    <ToastProvider>
      <ConnectorSignupCompleteInner />
    </ToastProvider>
  )
}
